import { useQueryClient } from "@tanstack/react-query";
import { VERSION_HISTORY_QUERY_KEYS } from "../../version-history/constants/versionHistory.constants";
import { MAPPING_VERSION_QUERY_KEYS } from "../constants/mappingVersion.constants";

/**
 * Drops every cache that answers "which version is active, and what does it contain".
 *
 * Creating, activating or deleting a version changes all of them server-side, so a flow that does
 * one of those must invalidate them together. Invalidating a subset is precisely what left
 * MappingHeaderCard showing the old version until a page refresh: the create flow dropped only
 * `nextVersionInfo`, so `default-version` kept serving the previous version indefinitely (the
 * global `refetchOnMount: false` in App.tsx means a mounted query never refires on its own --
 * `invalidateQueries` refetches it regardless, since that option only governs mount).
 *
 * `drawingToolId` narrows the two keys that carry it. The rest are matched by key prefix, because
 * their id is a *version* id the caller does not know (`latest-calculation`, `version`) or a
 * paginated tuple (`groupedHistory`). Only one mapping is on screen at a time, so the broader
 * match costs nothing. Prefixes do not collide: `["version"]` matches `["version", id]` but not
 * `["version-history", id]`, since array keys match element-by-element.
 */
export function useInvalidateVersionCaches() {
  const queryClient = useQueryClient();

  return (drawingToolId?: string): void => {
    queryClient.invalidateQueries(
      drawingToolId ? ["default-version", drawingToolId] : ["default-version"]
    );
    queryClient.invalidateQueries(
      drawingToolId
        ? [MAPPING_VERSION_QUERY_KEYS.nextVersionInfo, drawingToolId]
        : [MAPPING_VERSION_QUERY_KEYS.nextVersionInfo]
    );
    queryClient.invalidateQueries(["latest-calculation"]);
    queryClient.invalidateQueries(["version"]);
    queryClient.invalidateQueries(["version-history"]);
    queryClient.invalidateQueries([VERSION_HISTORY_QUERY_KEYS.groupedHistory]);
  };
}
